import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDPageContainer } from '../primitives/CDPageContainer';
import { CDLoanCosts, type LoanCostsData } from '../sections/CDLoanCosts';
import { CDOtherCosts, type OtherCostsData } from '../sections/CDOtherCosts';
import { CDTotalClosingCosts, type TotalClosingCostsData } from '../sections/CDTotalClosingCosts';

export interface CDPage2Data {
  loanCosts: Partial<LoanCostsData>;
  otherCosts: Partial<OtherCostsData>;
  totalClosingCosts: Partial<TotalClosingCostsData>;
}

export interface CDPage2Props {
  /** Page 2 data */
  data: Partial<CDPage2Data>;
  /** Total number of pages */
  totalPages?: number;
  /** Form ID for footer */
  formId?: string;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler for data updates */
  onChange?: (data: Partial<CDPage2Data>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDPage2 - Complete Page 2 of Closing Disclosure (Closing Cost Details)
 *
 * Contains:
 * - Page title "Closing Cost Details"
 * - Loan Costs (Sections A-D)
 * - Other Costs (Sections E-I)
 * - Total Closing Costs (Section J)
 * - Page footer
 */
export const CDPage2 = React.forwardRef<HTMLDivElement, CDPage2Props>(
  ({ data, totalPages = 5, formId, editable = false, onChange, className }, ref) => {
    const handleLoanCostsChange = (loanCosts: Partial<LoanCostsData>) => {
      onChange?.({ ...data, loanCosts });
    };

    const handleOtherCostsChange = (otherCosts: Partial<OtherCostsData>) => {
      onChange?.({ ...data, otherCosts });
    };

    const handleTotalClosingCostsChange = (totalClosingCosts: Partial<TotalClosingCostsData>) => {
      onChange?.({ ...data, totalClosingCosts });
    };

    return (
      <CDPageContainer
        ref={ref}
        pageNumber={2}
        totalPages={totalPages}
        formId={formId}
        className={cn(className)}
      >
        {/* Page Title */}
        <h2 className="cd-text-page-title mb-2">Closing Cost Details</h2>

        {/* Loan Costs (A-D) */}
        <CDLoanCosts
          data={data.loanCosts || {}}
          editable={editable}
          onChange={handleLoanCostsChange}
        />

        {/* Other Costs (E-I) */}
        <div className="mt-2">
          <CDOtherCosts
            data={data.otherCosts || {}}
            editable={editable}
            onChange={handleOtherCostsChange}
          />
        </div>

        {/* Total Closing Costs (J) */}
        <div className="mt-2">
          <CDTotalClosingCosts
            data={data.totalClosingCosts || {}}
            editable={editable}
            onChange={handleTotalClosingCostsChange}
          />
        </div>
      </CDPageContainer>
    );
  },
);

CDPage2.displayName = 'CDPage2';
